<ng-template #phoneErrorTpl let-control>
  @if (control.errors?.['required']) {
    EcoCash number is required.
  } @else {
    Enter a valid EcoCash number.
  }
</ng-template>

@if (compact()) {
  <!-- Compact, labelled panel (checkout dialog) -->
  <form nz-form nzLayout="vertical" [formGroup]="paymentForm" class="mt-4">
    <div class="flex flex-col py-4 px-6">
      <nz-form-item class="!mb-1">
        <nz-form-label class="!text-main-color !font-normal">EcoCash Phone Number</nz-form-label>
        <nz-form-control [nzErrorTip]="phoneErrorTpl">
          <nz-input-wrapper class="!bg-transparent">
            <span nzInputPrefix>
              <img class="h-4 w-6 object-contain" src="/images/ecocash-logo.png" alt="EcoCash" />
            </span>
            <input
              nz-input nzSize="large"
              type="text"
              maxlength="10"
              formControlName="phoneNumber"
              libNumericOnly
              placeholder="e.g. 0771234567"
            />
          </nz-input-wrapper>
        </nz-form-control>
      </nz-form-item>
      <p class="text-xs text-gray-500">Enter your registered EcoCash mobile number to proceed.</p>
    </div>
  </form>
} @else {
  <!-- Phone number entry (full-page checkout) -->
  <form nz-form nzLayout="vertical" [formGroup]="paymentForm" class="mt-4">
    <nz-form-item>
      <nz-form-control [nzErrorTip]="phoneErrorTpl">
        <nz-input-wrapper>
          <span nzInputPrefix>
            <img class="h-4 w-6 object-contain" src="/images/ecocash-logo.png" alt="EcoCash" />
          </span>
          <input
            nz-input nzSize="large"
            type="text"
            maxlength="10"
            formControlName="phoneNumber"
            libNumericOnly
            placeholder="EcoCash number (e.g. 0771234567)"
          />
        </nz-input-wrapper>
      </nz-form-control>
    </nz-form-item>
  </form>
}

<div class="flex flex-col gap-4 pt-3">
  @if (!compact()) {
    <p class="leading-normal text-xs">
      By paying this fee and clicking on "Pay", you agree to the
      <a class="cursor-pointer underline" target="_blank" rel="noopener noreferrer" href="">terms of service</a>
      and
      <a class="cursor-pointer underline" target="_blank" rel="noopener noreferrer" href="">Privacy policy</a>
    </p>
  }

  <button
    nz-button nzSize="large"
    nzType="primary"
    nzBlock
    type="button"
    [nzLoading]="isLoading()"
    [disabled]="
      isLoading() ||
      (payloadConfig().Amount ?? 0) > 100000000 ||
      (payloadConfig().Amount ?? 0) < minAmount ||
      paymentForm.invalid
    "
    (click)="generateReferenceAndSubmit(payloadConfig().feeId)"
  >
    {{ isLoading() ? 'Processing...' : 'Pay ' + payloadConfig().Currency + ' ' + payloadConfig().Amount }}
  </button>

  @if (!compact()) {
    <p class="leading-normal text-xs opacity-75">
      Payments are processed in USD. Payment provider fees may apply.
    </p>
  }
</div>

<!-- Payment status (html-driven nz-modal) -->
<nz-modal
  [nzVisible]="isPaymentInitiated()"
  [nzTitle]="'EcoCash payment'"
  [nzFooter]="null"
  [nzMaskClosable]="false"
  (nzOnCancel)="closeStatus()"
>
  <ng-container *nzModalContent>
    @if (isLoading()) {
      <div class="flex flex-col justify-center items-center py-10">
        <nz-spin nzSimple nzSize="large"></nz-spin>
        <p class="mt-6 text-gray-500">Please wait — approve the prompt on your phone...</p>
      </div>
    } @else if (checkoutInfo(); as info) {
      <!-- Failed -->
      @if (info.transactionStatus === EStatus.FAILED) {
        <div class="flex gap-4 w-full">
          <img src="/images/error.png" class="h-24" alt="Failed" />
          <div class="flex flex-col gap-2">
            <h3 class="text-gray-600 text-xl font-bold">Payment failed!</h3>
            <p class="text-sm text-gray-600">
              Payment of {{ info.currency?.symbol }} {{ info.amount }} failed. Consider trying another method.
            </p>
            <p class="text-sm text-gray-600">Invoice number: {{ info.invoiceNumber }}</p>
          </div>
        </div>
      }

      <!-- Pending -->
      @if (info.transactionStatus === EStatus.PENDING) {
        <div class="flex gap-4 w-full">
          <img src="/images/warning.png" class="h-24" alt="Pending" />
          <div class="flex flex-col gap-2">
            <h3 class="text-gray-600 text-xl font-bold">Payment pending!</h3>
            <p class="text-sm text-gray-600">Payment of {{ info.currency?.symbol }} {{ info.amount }}.</p>
            <p class="text-sm text-gray-600">Invoice number: {{ info.invoiceNumber }}</p>
          </div>
        </div>
      }

      <!-- Completed -->
      @if (info.transactionStatus === EStatus.COMPLETED) {
        <div class="flex gap-4 w-full">
          <img src="/images/check.png" class="h-24" alt="Success" />
          <div class="flex flex-col gap-2">
            <h3 class="text-gray-600 text-xl font-bold">Payment Successful!</h3>
            <p class="text-sm text-gray-600">
              Your wallet has been credited with {{ info.currency?.symbol }} {{ info.amount }}.
            </p>
            <div class="flex justify-between items-center gap-2">
              <p class="text-sm text-gray-600">Invoice number: {{ info.invoiceNumber }}</p>
              @if (!copied()) {
                <button type="button" (click)="copyToClipboard()" class="text-sm underline">Copy</button>
              } @else {
                <span class="text-xs">Copied!</span>
              }
            </div>
          </div>
        </div>
      }

      @if (info.callBackUrl) {
        <div class="flex justify-end mt-6">
          <a
            target="_top"
            [href]="info.callBackUrl + '?refNo=' + info.invoiceNumber + '&paymentChannel=' + info.paymentChannel"
          >
            <button
              nz-button nzSize="large"
              [nzType]="info.transactionStatus === EStatus.COMPLETED ? 'primary' : 'default'"
            >
              {{ info.transactionStatus === EStatus.COMPLETED ? 'Continue' : 'Go back' }}
            </button>
          </a>
        </div>
      }
    }
  </ng-container>
</nz-modal>
